import { DateWrapper } from '@trucktrax/trucktrax-common';
import { PlantDto, ProductLine } from '@trucktrax/trucktrax-ts-common';
import { PayloadAction } from '../../types';
import {
  OVERRIDE_MAP_FILTER,
  OPEN_MAP_FILTERS,
  RESET_MAP_FILTERS,
  TOGGLE_MAP_FILTER,
} from '../../constants/actionConstants';

export const mapFilterInitialState = {
  showTrucks: true,
  showPlants: false,
  showJobsites: false,
  showPlantsGeofences: false,
  showJobsitesGeofences: false,
  wasFilterOverridden: false,
  ordersStartDate: DateWrapper.now.startOfDay.date,
  ordersEndDate: DateWrapper.now.startOfDay.date,
  plantList: [] as PlantFilterOption[],
  // DEV-3820: the Incoming Materials map filters keep their own plant selection.
  incomingPlantFilters: [] as PlantFilterOption[],
  // DEV-4973: true while incomingPlantFilters was hydrated from a failed (or skipped) user
  // preferences read, so the all-checked display does not reflect the saved selection. A save
  // must then hold back the selection write until a read succeeds, not just clear shouldSave.
  incomingPlantFiltersUnverified: false,
  // DEV-5225: product lines unchecked in the IM ticket panel's Product Line filter, saved with
  // the Destination Plants selection. A save writes them only while they are verified (they
  // match the region's saved selection) or edited (the user changed them since the last save),
  // so an all-checked default never overwrites a saved selection.
  incomingDeselectedProductLines: [] as ProductLine[],
  incomingProductLinesVerified: false,
  incomingProductLinesEdited: false,
  shouldSave: false,
  showExtTrucks: false,
  isOpen: false,
};

export type MapFilterState = typeof mapFilterInitialState;
export type MapFilterStateKey = keyof typeof mapFilterInitialState;

export type MapFiltersPayload = {
  [Key in MapFilterStateKey]: MapFilterState[Key];
};

export interface MapFilterOrdersDateRange {
  ordersStartDate?: Date;
  ordersEndDate?: Date;
}

export interface MapFiltersIsOpenPayload {
  isOpen: boolean;
}

export type MapFiltersAllowedPayloads =
  | (MapFiltersPayload & MapFilterOrdersDateRange)
  | MapFiltersIsOpenPayload
  | undefined;
export const mapFilters = (
  state = mapFilterInitialState,
  action: PayloadAction<
    MapFiltersAllowedPayloads,
    'TOGGLE_MAP_FILTER' | 'OPEN_MAP_FILTER' | 'OVERRIDE_MAP_FILTER' | 'RESET_MAP_FILTERS'
  >,
): MapFilterState => {
  switch (action.type) {
    case TOGGLE_MAP_FILTER:
      return {
        ...state,
        ...action.payload,
        wasFilterOverridden: false,
      };
    case OPEN_MAP_FILTERS:
      return {
        ...state,
        isOpen: action.payload?.isOpen ?? state.isOpen,
        wasFilterOverridden: false,
      };
    case OVERRIDE_MAP_FILTER: {
      const payload = action.payload as MapFiltersPayload;
      const wasFilterOverridden =
        state.wasFilterOverridden ||
        Object.keys(payload).some(
          key => state[key as MapFilterStateKey] !== payload[key as MapFilterStateKey],
        );
      return {
        ...state,
        ...action.payload,
        wasFilterOverridden,
      };
    }
    case RESET_MAP_FILTERS:
      mapFilterInitialState.plantList = state.plantList.map(
        plant =>
          ({
            ...plant,
            isSelected: true,
          }) as PlantFilterOption,
      );
      return {
        ...mapFilterInitialState,
      };
    default:
      return state;
  }
};

export interface PlantFilterOption extends Pick<PlantDto, 'url' | 'id' | 'name' | 'url'> {
  isSelected: boolean;
}
